import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@izicvs/ui";
import { cn } from "@izicvs/utils";
import { t } from "@lingui/macro";
import {
  ArrowSquareOut,
  Kanban,
  Plus,
  ReadCvLogo,
  Target,
  TrashSimple,
} from "@phosphor-icons/react";
import { motion } from "framer-motion";
import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router";

import { ConfirmDelete } from "@/client/components/confirm-delete";
import { ThemeSwitch } from "@/client/components/theme-switch";
import { toast } from "@/client/hooks/use-toast";
import type { Application, ApplicationStatus } from "@/client/services/applications";
import {
  APPLICATION_STATUSES,
  useApplications,
  useApplicationStats,
  useCreateApplication,
  useDeleteApplication,
  useUpdateApplication,
} from "@/client/services/applications";

import { LoadingPanel } from "../_components/loading-panel";
import { NotificationsBell } from "../_components/notifications";

const COLUMN_ACCENT: Record<ApplicationStatus, string> = {
  to_apply: "bg-secondary",
  applied: "bg-info",
  interview: "bg-warning",
  offer: "bg-success",
  rejected: "bg-error",
};

const ApplicationCard = ({
  application,
  statusLabels,
  onMove,
  onDelete,
  onOpenAnalysis,
  onOpenResume,
}: {
  application: Application;
  statusLabels: Record<ApplicationStatus, string>;
  onMove: (id: string, status: ApplicationStatus) => void;
  onDelete: (id: string) => void;
  onOpenAnalysis: (id: string) => void;
  onOpenResume: (id: string) => void;
}) => (
  <div
    draggable
    className="grid cursor-grab gap-y-2 rounded-xl border bg-background p-3 shadow-sm transition-shadow hover:shadow-md active:cursor-grabbing"
    onDragStart={(event) => {
      event.dataTransfer.setData("text/plain", application.id);
    }}
  >
    <div className="flex items-start justify-between gap-x-2">
      <div className="min-w-0">
        <p className="line-clamp-2 text-sm font-medium">{application.jobTitle}</p>
        <p className="text-xs opacity-60">
          {[application.company, application.location].filter(Boolean).join(" · ")}
        </p>
      </div>
      <ConfirmDelete
        title={t`Delete this application?`}
        onConfirm={() => {
          onDelete(application.id);
        }}
      >
        <Button size="icon" variant="ghost" className="size-7 shrink-0">
          <TrashSimple size={14} />
        </Button>
      </ConfirmDelete>
    </div>

    <div className="flex items-center gap-x-2">
      <Select
        value={application.status}
        onValueChange={(status) => {
          onMove(application.id, status as ApplicationStatus);
        }}
      >
        <SelectTrigger className="h-7 flex-1 rounded-md text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {APPLICATION_STATUSES.map((status) => (
            <SelectItem key={status} value={status}>
              {statusLabels[status]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {application.url && (
        <Button asChild size="icon" variant="ghost" className="size-7 shrink-0">
          <a href={application.url} target="_blank" rel="noreferrer noopener">
            <ArrowSquareOut size={14} />
          </a>
        </Button>
      )}
    </div>

    {(application.analysisId || application.resumeId) && (
      <div className="flex flex-wrap gap-1.5">
        {application.analysisId && (
          <Button
            size="sm"
            variant="ghost"
            className="h-7 px-2 text-xs"
            onClick={() => {
              onOpenAnalysis(application.analysisId!);
            }}
          >
            <Target className="mr-1" size={13} />
            {t`Analysis`}
          </Button>
        )}
        {application.resumeId && (
          <Button
            size="sm"
            variant="ghost"
            className="h-7 px-2 text-xs"
            onClick={() => {
              onOpenResume(application.resumeId!);
            }}
          >
            <ReadCvLogo className="mr-1" size={13} />
            {t`Resume`}
          </Button>
        )}
      </div>
    )}
  </div>
);

// Outcome analytics: proves which resume actually gets results.
const OutcomesPanel = () => {
  const { stats } = useApplicationStats();

  if (!stats || stats.sent === 0) return null;

  const callbacks = stats.funnel.interview + stats.funnel.offer;

  const Stat = ({ value, label, sub }: { value: React.ReactNode; label: string; sub?: string }) => (
    <div className="rounded-2xl border p-4 shadow-sm">
      <p className="text-3xl font-bold tabular-nums">{value}</p>
      <p className="mt-0.5 text-sm font-medium">{label}</p>
      {sub && <p className="text-xs opacity-60">{sub}</p>}
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat value={stats.sent} label={t`Applications sent`} />
        <Stat value={callbacks} label={t`Callbacks`} sub={t`interviews and offers`} />
        <Stat value={`${stats.callbackRate}%`} label={t`Callback rate`} sub={t`callbacks / sent`} />
        <Stat value={`${stats.offerRate}%`} label={t`Offer rate`} />
      </div>

      {stats.byResume.length > 1 && (
        <div className="overflow-hidden rounded-2xl border">
          <div className="border-b bg-secondary-accent/20 p-3 text-sm font-semibold">
            {t`Callback rate by resume`}
          </div>
          <div className="divide-y">
            {stats.byResume.map((resume, index) => (
              <div
                key={resume.resumeId ?? "none"}
                className="flex items-center gap-x-3 p-3 text-sm"
              >
                <span className="min-w-0 flex-1 truncate">
                  {resume.title || t`(no resume linked)`}
                </span>
                <span className="shrink-0 text-xs opacity-60">{t`${resume.sent} sent`}</span>
                <div className="flex w-32 shrink-0 items-center gap-x-2">
                  <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-secondary">
                    <div
                      className={cn(
                        "h-full rounded-full",
                        index === 0 ? "bg-success" : "bg-primary",
                      )}
                      style={{ width: `${resume.callbackRate}%` }}
                    />
                  </div>
                  <span className="w-9 text-right font-semibold tabular-nums">
                    {resume.callbackRate}%
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export const ApplicationsPage = () => {
  const navigate = useNavigate();
  const { applications, loading } = useApplications();
  const { create } = useCreateApplication();
  const { update } = useUpdateApplication();
  const { remove } = useDeleteApplication();

  const onOpenAnalysis = (id: string) =>
    void navigate("/dashboard/jobs", { state: { openAnalysisId: id } });
  const onOpenResume = (id: string) => void navigate(`/builder/${id}`);

  const [jobTitle, setJobTitle] = useState("");
  const [company, setCompany] = useState("");
  const [dragOver, setDragOver] = useState<ApplicationStatus | null>(null);

  const statusLabels: Record<ApplicationStatus, string> = {
    to_apply: t`To apply`,
    applied: t`Applied`,
    interview: t`Interview`,
    offer: t`Offer`,
    rejected: t`Rejected`,
  };

  const onAdd = async () => {
    if (!jobTitle.trim() || !company.trim()) return;
    try {
      await create({ jobTitle: jobTitle.trim(), company: company.trim() });
      setJobTitle("");
      setCompany("");
    } catch (error) {
      toast({
        variant: "error",
        title: t`Oops, the server returned an error.`,
        description: (error as Error).message,
      });
    }
  };

  const onMove = (id: string, status: ApplicationStatus) => void update({ id, status });

  return (
    <>
      <Helmet>
        <title>
          {t`Applications`} - {t`Izicvs`}
        </title>
      </Helmet>

      <div className="space-y-6">
        {/* Header: title + intro, with the shared notification + theme cluster. */}
        <div className="flex items-start justify-between gap-4">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-1"
          >
            <h1 className="flex items-center gap-x-2 text-3xl font-bold tracking-tight lg:text-4xl">
              <Kanban weight="fill" className="text-primary" />
              {t`Applications`}
            </h1>
            <p className="max-w-xl text-sm opacity-75">
              {t`Track every application through its pipeline, from "to apply" to "offer".`}
            </p>
          </motion.div>

          <div className="flex shrink-0 items-center gap-x-3">
            <NotificationsBell />
            <ThemeSwitch />
          </div>
        </div>

        {loading ? (
          <LoadingPanel />
        ) : (
          <>
            <OutcomesPanel />

            <div className="flex flex-wrap items-end gap-2 rounded-2xl border bg-secondary-accent/20 p-4">
              <Input
                value={jobTitle}
                placeholder={t`Job title`}
                className="w-full sm:w-56"
                onChange={(event) => {
                  setJobTitle(event.target.value);
                }}
                onKeyDown={(event) => event.key === "Enter" && onAdd()}
              />
              <Input
                value={company}
                placeholder={t`Company`}
                className="w-full sm:w-48"
                onChange={(event) => {
                  setCompany(event.target.value);
                }}
                onKeyDown={(event) => event.key === "Enter" && onAdd()}
              />
              <Button disabled={!jobTitle.trim() || !company.trim()} onClick={onAdd}>
                <Plus className="mr-2" />
                {t`Add`}
              </Button>
            </div>

            <div className="-mx-2 flex snap-x snap-mandatory gap-3 overflow-x-auto px-2 pb-2 md:mx-0 md:grid md:grid-cols-2 md:overflow-visible md:px-0 md:pb-0 xl:grid-cols-5">
              {APPLICATION_STATUSES.map((status) => {
                const items = (applications ?? []).filter(
                  (application) => application.status === status,
                );
                return (
                  <div
                    key={status}
                    className={cn(
                      "grid w-[80vw] shrink-0 snap-start content-start gap-y-2 rounded-xl border p-3 transition-colors sm:w-[46vw] md:w-auto",
                      dragOver === status && "border-primary/50 bg-primary/5",
                    )}
                    onDragOver={(event) => {
                      event.preventDefault();
                      setDragOver(status);
                    }}
                    onDragLeave={() => {
                      setDragOver((current) => (current === status ? null : current));
                    }}
                    onDrop={(event) => {
                      event.preventDefault();
                      const id = event.dataTransfer.getData("text/plain");
                      if (id) onMove(id, status);
                      setDragOver(null);
                    }}
                  >
                    <div className="flex items-center gap-x-2">
                      <span className={cn("size-2 rounded-full", COLUMN_ACCENT[status])} />
                      <span className="text-sm font-semibold">{statusLabels[status]}</span>
                      <span className="ml-auto rounded-full bg-secondary px-2 text-xs opacity-70">
                        {items.length}
                      </span>
                    </div>
                    {items.length === 0 && (
                      <p className="rounded-lg border border-dashed py-6 text-center text-xs opacity-40">
                        {t`Drop cards here`}
                      </p>
                    )}
                    {items.map((application) => (
                      <ApplicationCard
                        key={application.id}
                        application={application}
                        statusLabels={statusLabels}
                        onMove={onMove}
                        onDelete={(id) => void remove(id)}
                        onOpenAnalysis={onOpenAnalysis}
                        onOpenResume={onOpenResume}
                      />
                    ))}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </>
  );
};
